/** Локализация интерфейса.
 *
 * Своя реализация вместо библиотеки: словарь плоский, подстановок мало,
 * а i18next весит больше всего остального приложения вместе взятого.
 * Для Mini App, который должен открываться мгновенно, это не размен.
 *
 * Язык берётся из профиля Telegram и переопределяется настройкой игрока.
 * Русский — язык по умолчанию: ключ, которого нет в английском словаре,
 * покажется по-русски, а не как `game.title`.
 */

import { ru } from './ru'
import { en } from './en'

export type Language = 'ru' | 'en'

const DICTIONARIES: Record<Language, Record<string, string>> = { ru, en }

let current: Language = 'ru'

export function setLanguage(language: Language): void {
  current = language
  // Атрибут lang нужен браузеру для переносов и экранного диктора, но сам
  // словарь от DOM не зависит — иначе его нельзя было бы тестировать.
  if (typeof document !== 'undefined') {
    document.documentElement.lang = language
  }
}

export function getLanguage(): Language {
  return current
}

/** Поддерживается ли язык; всё остальное сводится к русскому. */
export function normalizeLanguage(code: string | undefined | null): Language {
  return code?.toLowerCase().startsWith('en') ? 'en' : 'ru'
}

/**
 * Перевести ключ, подставив значения вида `{name}`.
 *
 * Отсутствующий ключ возвращается как есть — так пропущенный перевод видно
 * в интерфейсе сразу, а не через месяц в отзывах.
 */
export function t(key: string, params?: Record<string, string | number>): string {
  const template = DICTIONARIES[current][key] ?? DICTIONARIES.ru[key] ?? key
  if (!params) return template
  return template.replace(/\{(\w+)\}/g, (match, name) =>
    name in params ? String(params[name]) : match,
  )
}

/**
 * Форма слова по числу, взятая из словаря: `plural.days` → «день|дня|дней».
 *
 * Формы лежат в словаре, а не в коде экрана, по той же причине, что и всё
 * остальное: английский вариант обязан меняться вместе с русским, а не
 * подгоняться отдельной правкой в другом месте.
 */
export function pluralOf(key: string, count: number): string {
  const forms = t(key).split('|')
  return plural(count, [forms[0], forms[1] ?? forms[0], forms[2] ?? forms[1] ?? forms[0]])
}

/** Склонение числительных: 1 день, 2 дня, 5 дней. */
export function plural(count: number, forms: [string, string, string]): string {
  if (current === 'en') return forms[count === 1 ? 0 : 1]

  const mod10 = count % 10
  const mod100 = count % 100
  if (mod10 === 1 && mod100 !== 11) return forms[0]
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1]
  return forms[2]
}
